//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { events } from "@nuskin/ns-util";
import { NsSlottedDropdown, NsSpinner } from "@nuskin/design-components";
import { fromJsonString, isNullOrEmpty } from "@nuskin/ns-common-lib";

import debounce from "lodash/debounce";

import NsProductList from "./NsProductList.vue";

const PRODUCT_COUNT = "%count%";
const DISTRIBUTOR_ONLY_SORT_OPTIONS = ["pointHighToLow", "pointLowToHigh"];

/**
 * NsProductListSortable
 * Events
 * @event sort-by-select  Fired when a dropdown item is selected with (id) => {}
 */

export default {
  name: "NsProductListSortable",
  components: { NsSlottedDropdown, NsSpinner, NsProductList },
  props: {
    /**
     * Determines if the product cards should be displayed side-by-side in mobile
     */
    mobileSideBySide: {
      type: Boolean,
      default: true
    },
    /**
     * 'Products' label for translations. Should include '%count%'. See default
     * for English example
     */
    productsLabel: {
      type: String,
      default: "%count% Products"
    },
    /**
     * The placehoder text in the sortBy dropdown
     */
    selectPlaceholder: {
      type: String,
      default: ""
    },
    /**
     * Array of skus for product list. Takes array of strings or objects
     * ([sku] || [{ sku }])
     */
    skus: {
      type: [String, Array],
      default: () => []
    },
    /**
     * Object containing the search scores for sorting
     */
    skuSearchScores: {
      type: Object,
      default: null
    },
    /**
     * SortBy label
     */
    sortByLabel: {
      type: String,
      default: ""
    },
    /**
     * SortBy options. Array of objects:  [{ id, title, sort, order }]
     */
    sortByOptions: {
      type: Array,
      default: () => []
    },
    /**
     * Set sortBy selected option programmically. Should match one of
     * sortByOptions[n].id
     */
    sortBySelected: {
      type: String,
      default: ""
    },
    /**
     * Title of list
     */
    title: {
      type: String,
      default: ""
    },
    /**
     * Initial number of products displayed and number added per "Load More" button click. Set to 0 to disable.
     */
    skusPerLoad: {
      type: Number,
      default: 20
    }
  },
  data: function() {
    return {
      products: {},
      localSku: [],
      sortedSkus: [],
      sortByValue: this.sortBySelected
    };
  },
  computed: {
    sortOptions() {
      return this.sortByOptions.filter(option => {
        // exclude distributor_only filter option if user != distributor
        if (
          DISTRIBUTOR_ONLY_SORT_OPTIONS.includes(option.id) &&
          !this.$NsProductUserService.isDistributor
        ) {
          return false;
        }
        // exclude sorting by best match unless scores provided
        return option.id !== "bestMatch" || !!this.skuSearchScores;
      });
    },
    showSpinnerOnFirstLoad() {
      return (
        this.sortedSkus.length <= this.skusPerLoad &&
        this.$NsProductDataService.batches.some(
          batch =>
            !["success", "failed"].includes(batch.status) &&
            batch.skus.some(batchSku => this.localSkus.includes(batchSku))
        )
      );
    },
    productCount() {
      const productsFound = this.$NsProductDataService.findProducts(
        this.sortedSkus
      );
      return productsFound.length;
    },
    productCountLabel() {
      return this.productsLabel.replace(PRODUCT_COUNT, this.productCount);
    }
  },
  watch: {
    skus: {
      immediate: true,
      handler(skus) {
        this.localSkus = fromJsonString(skus, []).map(item => {
          if (typeof item === "object") {
            return item.sku || item.SKU;
          }
          return item;
        });
        this.sortBy();
      }
    },
    sortBySelected(value) {
      this.sortByValue = value;
    },
    sortByValue() {
      this.sortBy();
    }
  },
  methods: {
    /**
     * Saves the products' data in local state, so it can sort by this data
     * @pram <Product> product
     */
    handleProductUpdate(product) {
      if (product) {
        this.products = {
          ...this.products,
          [product.sku]: { ...this.products[product.sku], ...product }
        };
      }
    },

    /**
     * Used to get the price type for the current user
     */
    getPriceType() {
      return this.$NsProductDataService.getPriceType();
    },

    /**
     * Used to get the price to sort a produce
     * @param product
     * @returns {number}
     * @private
     */
    getProductSortingPrice(product, priceType) {
      if (isNullOrEmpty(product)) {
        return 0;
      }

      product.setPriceAndPvFromType(priceType);

      const productPrice = product.isBase()
        ? product.getMinPrice()
        : product.getPrice(); // don't use .price here
      return productPrice;
    },

    /**
     * Used to get the points to sort a produce
     * @param product
     * @returns {number}
     * @private
     */
    getProductSortingPv(product, priceType) {
      if (isNullOrEmpty(product)) {
        return 0;
      }

      product.setPriceAndPvFromType(priceType);

      const productPv = product.isBase() ? product.getMinPv() : product.pv;
      return productPv;
    },

    sortSelect(value) {
      if (!this.$NsProductDataService.check(this.localSkus)) {
        this.$NsProductDataService.queue(this.localSkus);
      }

      this.sortByValue = value;
      this.$emit("sort-by-select", value);
      events.publish(events.shop.SEARCH_SORT_DROPDOWN);
    },

    sortBy: debounce(function() {
      // Find sortOption
      const sortOption = this.sortOptions.find(
        option => option.id === this.sortByValue
      );

      // if default or no sort, reset to current skus
      if (!sortOption || !sortOption.sort || sortOption.sort === "default") {
        this.sortedSkus = [...this.localSkus];
        return;
      }

      const sort = sortOption.sort;
      const order = sortOption.order;

      const products = this.$NsProductDataService
        .findProducts(this.localSkus, false)
        .reduce((a, p) => ({ ...a, [p.sku]: p }), {});

      const sortedSkus = [...this.localSkus].sort((x, y) => {
        //make sure to get sku
        //@note every product gets updated every product card load
        const xSku = typeof x === "object" ? x.sku : x;
        const ySku = typeof y === "object" ? y.sku : y;

        // get full product objects
        const a = products[xSku] || {};
        const b = products[ySku] || {};

        if (sort === "title") {
          return order === "desc"
            ? (b[sort] || "").localeCompare(a[sort] || "")
            : (a[sort] || "").localeCompare(b[sort] || "");
        } else if (sort === "price") {
          const priceType = this.getPriceType();
          return order === "desc"
            ? this.getProductSortingPrice(b, priceType) -
                this.getProductSortingPrice(a, priceType)
            : this.getProductSortingPrice(a, priceType) -
                this.getProductSortingPrice(b, priceType);
        } else if (sort === "pv") {
          const priceType = this.getPriceType();
          return order === "desc"
            ? this.getProductSortingPv(b, priceType) -
                this.getProductSortingPv(a, priceType)
            : this.getProductSortingPv(a, priceType) -
                this.getProductSortingPv(b, priceType);
        } else if (sort === "best") {
          //fallback condition - if product is not updated yet, use the search scores from NsSearchResults
          const aSearchScore =
            (this.skuSearchScores[a.sku]
              ? this.skuSearchScores[a.sku]
              : this.skuSearchScores[xSku]) || 0;
          const bSearchScore =
            (this.skuSearchScores[b.sku]
              ? this.skuSearchScores[b.sku]
              : this.skuSearchScores[ySku]) || 0;

          return bSearchScore - aSearchScore;
        } else {
          return order === "desc"
            ? sortValue(b[sort]) - sortValue(a[sort])
            : sortValue(a[sort]) - sortValue(b[sort]);
        }
      });
      this.sortedSkus = sortedSkus;

      function sortValue(item) {
        if (typeof item === "undefined" || item === null) {
          return 0;
        }

        return item;
      }
    }, 250)
  }
};
